import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import { loadConversations, loadMoreConversations, loadConversation, renameConversation, deleteConversation } from "./hermes.action";
import type { HermesState, HermesRootState, HydrationToolCall, StreamingToolCall, StructuredBlock, CompaniesBlock, ConversationStartedPayload, TextDeltaPayload, ToolCallStartPayload, ToolCallResultPayload, FollowUpsPayload, CompaniesPayload, StreamStartedPayload, StreamEndedPayload, StreamErroredPayload, StreamInProgressPayload } from "@containers/Hermes/types";

// Shown when polling gives up on a background response — either the worker never
// delivered (pollTimedOut) or the fetch failed repeatedly (pollFailed).
const POLL_ERROR_MESSAGE = "Something went wrong. Please try asking again.";

// Sentinel id for a brand-new conversation's stream, used until the backend
// sends `conversation_started` with the real id.
const PENDING_CONVERSATION_ID = "__pending__";

// True when the in-flight stream belongs to the conversation the user is
// currently viewing. A brand-new conversation (streamingConversationId ===
// PENDING) matches the landing view, where activeConversationId is still null.
const streamMatchesActiveView = (state: HermesState): boolean => state.streamingConversationId !== null && state.streamingConversationId === (state.activeConversationId ?? PENDING_CONVERSATION_ID);

// The backend creates the assistant row with empty content at the start of a
// turn (so tool-call audit rows can FK to it) and only fills it in on `done`.
// GET /messages returns that placeholder verbatim, so loading a conversation
// mid-stream would surface an empty assistant bubble — and, once the stream
// commits, a row whose id collides with the placeholder's. A row with no text
// AND no structured blocks is that placeholder; drop it. (A finished chart/table
// turn keeps its structured_blocks, so it survives this filter.)
// GET /messages may deliver structured_blocks as a JSON string or an array;
// normalize before inspecting so the placeholder check is correct either way.
const normalizeStructuredBlocks = (raw: unknown): StructuredBlock[] => (typeof raw === "string" ? JSON.parse(raw) : ((raw as StructuredBlock[] | null | undefined) ?? []));

const isInFlightAssistantPlaceholder = (msg: { role: string; content: string; structured_blocks: unknown }): boolean => msg.role === "assistant" && (msg.content ?? "").trim() === "" && normalizeStructuredBlocks(msg.structured_blocks).length === 0;

const streamingToolCallsToHydration = (toolCallsById: Record<string, StreamingToolCall>): HydrationToolCall[] =>
  Object.values(toolCallsById).map((tc) => ({
    tool_name: tc.tool,
    label: tc.label,
    summary: tc.summary,
    duration_ms: tc.durationMs ?? 0,
    error: tc.error ?? null,
    created_at: new Date(tc.startedAt).toISOString(),
  }));

const commitStreamingMessage = (state: HermesState, messageId: number): void => {
  if (!state.streamingMessage) return;
  if (!state.streamingMessage.contentSoFar && messageId === 0) {
    return;
  }
  state.messages.push({
    id: messageId,
    role: "assistant",
    content: state.streamingMessage.contentSoFar,
    structured_blocks: state.streamingMessage.structuredBlocks,
    tool_calls: streamingToolCallsToHydration(state.streamingMessage.toolCalls),
    companies: state.streamingMessage.companies,
    created_at: new Date().toISOString(),
  });
  state.streamingMessage = null;
};

// End a poll in the error state. Clears BOTH polling and loading ids so an
// already-dispatched poll tick can't slip through the stale-load guard and
// resurrect awaiting_response after we've given up.
const failPolling = (state: HermesState): void => {
  state.status = "error";
  state.error = POLL_ERROR_MESSAGE;
  state.pollingConversationId = null;
  state.loadingConversationId = null;
  state.streamingMessage = null;
};

const initialState: HermesState = {
  conversations: [],
  conversationsLoading: false,
  conversationsOffset: 0,
  conversationsHasMore: false,

  activeConversationId: null,
  activeConversation: null,
  messages: [],

  streamingMessage: null,
  streamingConversationId: null,
  pollingConversationId: null,
  loadingConversationId: null,

  status: "idle",
  error: null,
};

const hermesSlice = createSlice({
  name: "hermes",
  initialState: { ...initialState },
  reducers: {
    streamStarted: (state, action: PayloadAction<StreamStartedPayload>) => {
      state.messages.push({
        id: Date.now(),
        role: "user",
        content: action.payload.userMessageText,
        structured_blocks: [],
        tool_calls: [],
        created_at: new Date().toISOString(),
      });
      state.streamingMessage = {
        contentSoFar: "",
        structuredBlocks: [],
        toolCalls: {},
        companies: [],
      };
      state.streamingConversationId = action.payload.conversationId ?? PENDING_CONVERSATION_ID;
      state.pollingConversationId = null;
      state.status = "streaming";
      state.error = null;
    },

    conversationStarted: (state, action: PayloadAction<ConversationStartedPayload>) => {
      const { conversation_id } = action.payload;

      // Migrate the stream from its pending id to the real one so it stays
      // matched to its conversation no matter what the user is viewing.
      if (state.streamingConversationId === PENDING_CONVERSATION_ID) {
        state.streamingConversationId = conversation_id;
      }

      // Only adopt the new id as the active view if the user is still sitting on
      // the new-chat view; if they've navigated to another conversation, leave
      // their view alone (the stream keeps running in the background).
      if (state.activeConversationId === null) {
        state.activeConversationId = conversation_id;
      }

      const alreadyInList = state.conversations.some((c) => c.id === conversation_id);
      if (!alreadyInList) {
        const firstMessage = state.messages[0]?.content ?? "";
        const title = firstMessage.length > 30 ? firstMessage.slice(0, 30).trimEnd() + "..." : firstMessage || "New conversation";
        state.conversations.unshift({
          id: conversation_id,
          title,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });
      }
    },

    textDeltaAppended: (state, action: PayloadAction<TextDeltaPayload>) => {
      if (state.streamingMessage) {
        state.streamingMessage.contentSoFar += action.payload.delta;
      }
    },

    // The answer starts here: what streamed before it was the model working
    // (sometimes a full draft it then writes again), which the saved answer
    // leaves out.
    textReset: (state) => {
      if (state.streamingMessage) {
        state.streamingMessage.contentSoFar = "";
      }
    },

    toolCallStarted: (state, action: PayloadAction<ToolCallStartPayload>) => {
      if (!state.streamingMessage) return;
      const { id, tool, label, summary } = action.payload;
      state.streamingMessage.toolCalls[id] = {
        id,
        tool,
        label: label ?? tool,
        summary: summary ?? "",
        durationMs: undefined,
        error: undefined,
        startedAt: Date.now(),
      };
    },

    toolCallResulted: (state, action: PayloadAction<ToolCallResultPayload>) => {
      if (!state.streamingMessage) return;
      const { id, duration_ms, error } = action.payload;
      const tc = state.streamingMessage.toolCalls[id];
      if (tc) {
        tc.durationMs = duration_ms;
        tc.error = error;
      }
    },

    structuredBlockReceived: (state, action: PayloadAction<StructuredBlock>) => {
      if (state.streamingMessage) {
        state.streamingMessage.structuredBlocks.push(action.payload);
      }
    },

    companiesReceived: (state, action: PayloadAction<CompaniesPayload>) => {
      if (!state.streamingMessage) return;
      const existing = new Set(state.streamingMessage.companies.map((c) => c.name.toLowerCase()));
      for (const company of action.payload.items ?? []) {
        const key = company.name?.toLowerCase();
        if (!key || existing.has(key)) continue;
        existing.add(key);
        state.streamingMessage.companies.push(company);
      }
    },

    followUpsReceived: (state, action: PayloadAction<FollowUpsPayload>) => {
      if (state.streamingMessage) {
        state.streamingMessage.structuredBlocks.push({
          type: "follow_up_suggestions",
          suggestions: action.payload.suggestions,
        });
      }
    },

    streamEnded: (state, action: PayloadAction<StreamEndedPayload>) => {
      const { message_id, conversation_id, title, content } = action.payload;

      // Only fold the streamed message into the visible thread if the user is
      // viewing the conversation it belongs to. Otherwise it finished in the
      // background — drop the buffer; the backend has persisted it and a later
      // loadConversation will pick it up.
      if (streamMatchesActiveView(state)) {
        // Show the answer as saved, so it doesn't change on the next load.
        if (state.streamingMessage && typeof content === "string" && content.trim()) state.streamingMessage.contentSoFar = content;
        commitStreamingMessage(state, message_id);
        state.activeConversationId = conversation_id;
        state.status = "idle";
        state.error = null;
      } else {
        state.streamingMessage = null;
      }
      state.streamingConversationId = null;

      // `title` is only present when this closes out a brand-new conversation's
      // first turn — the backend generated it server-side. Apply it straight to
      // the sidebar entry without a follow-up API call. Check for the key being
      // present rather than truthiness, so a (theoretical) empty-string title
      // isn't mistaken for the field being absent.
      const hasTitle = title !== undefined;
      const idx = state.conversations.findIndex((c) => c.id === conversation_id);
      if (idx !== -1) {
        const updated = {
          ...state.conversations[idx],
          ...(hasTitle ? { title } : {}),
          updated_at: new Date().toISOString(),
        };
        state.conversations.splice(idx, 1);
        state.conversations.unshift(updated);
      }
      if (hasTitle && state.activeConversation && state.activeConversation.id === conversation_id) {
        state.activeConversation.title = title;
      }
    },

    streamErrored: (state, action: PayloadAction<StreamErroredPayload>) => {
      if (streamMatchesActiveView(state)) {
        commitStreamingMessage(state, 0);
        state.status = "error";
        state.error = action.payload.message;
      } else {
        state.streamingMessage = null;
      }
      state.streamingConversationId = null;
    },

    streamAborted: (state) => {
      if (streamMatchesActiveView(state)) {
        commitStreamingMessage(state, 0);
        state.status = "idle";
        state.error = null;
      } else {
        state.streamingMessage = null;
      }
      state.streamingConversationId = null;
    },

    // The backend rejected a send with 409 stream_in_progress: a previous
    // response for this conversation is still being generated in the background.
    // Roll back the optimistic user message + streaming buffer we added on
    // streamStarted and fall into the poll-until-ready state instead of showing
    // a generic error.
    streamInProgressDetected: (state, action: PayloadAction<StreamInProgressPayload>) => {
      state.streamingMessage = null;
      state.streamingConversationId = null;
      // Roll the optimistic user bubble back out of the thread — the send was
      // rejected, so it isn't a real message. The composer restores the typed
      // text (see useHermesStream returning false) so nothing is lost.
      const last = state.messages[state.messages.length - 1];
      if (last && last.role === "user") state.messages.pop();
      state.status = "awaiting_response";
      state.pollingConversationId = action.payload.conversationId;
      // Match the poll target so its own poll results aren't dropped by the
      // stale-load guard in loadConversation.fulfilled.
      state.loadingConversationId = action.payload.conversationId;
      state.error = null;
    },

    // Polling exceeded its 12-min ceiling; the background worker likely crashed.
    pollTimedOut: (state) => {
      failPolling(state);
    },

    // Polling failed to reach the server too many times in a row.
    pollFailed: (state) => {
      failPolling(state);
    },

    conversationCleared: (state) => {
      state.activeConversationId = null;
      state.activeConversation = null;
      state.messages = [];
      state.streamingMessage = null;
      state.streamingConversationId = null;
      state.pollingConversationId = null;
      state.loadingConversationId = null;
      state.status = "idle";
      state.error = null;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loadConversations.pending, (state) => {
        state.conversationsLoading = true;
      })
      .addCase(loadConversations.fulfilled, (state, action) => {
        state.conversations = action.payload.conversations;
        state.conversationsOffset = action.payload.conversations.length;
        state.conversationsHasMore = action.payload.has_more;
        state.conversationsLoading = false;
      })
      .addCase(loadConversations.rejected, (state) => {
        state.conversationsLoading = false;
      })

      .addCase(loadMoreConversations.fulfilled, (state, action) => {
        state.conversations = [...state.conversations, ...action.payload.conversations];
        state.conversationsOffset += action.payload.conversations.length;
        state.conversationsHasMore = action.payload.has_more;
      })

      .addCase(loadConversation.pending, (state, action) => {
        // Don't flash "loading" while polling the same conversation for its
        // background response — that would tear down the thinking indicator on
        // every poll tick. A load for a *different* conversation still loads.
        const isSelfPoll = state.status === "awaiting_response" && state.pollingConversationId === action.meta.arg;
        if (!isSelfPoll) {
          // Only genuine loads (initial open / navigation) claim the stale-load
          // guard. If poll ticks also stamped it, a tick firing right after the
          // user navigates away would overwrite the guard and cause the new
          // conversation's result to be dropped as "stale".
          state.loadingConversationId = action.meta.arg;
          state.status = "loading";
        }
        state.error = null;
      })
      .addCase(loadConversation.fulfilled, (state, action) => {
        // Drop a stale result: the user navigated (or a newer poll fired) after
        // this fetch went out, so applying it would clobber the current view.
        if (action.meta.arg !== state.loadingConversationId) return;

        const conversationId = action.payload.conversation.id;
        // Was this fetch a steady poll tick for the same conversation? If so and
        // the response still hasn't landed, we leave `messages` untouched to
        // avoid churning references (which would re-render/flicker the thread).
        const wasSelfPoll = state.status === "awaiting_response" && state.pollingConversationId === conversationId;

        state.activeConversationId = conversationId;
        state.activeConversation = action.payload.conversation;

        // Only the last row decides whether the response is still in flight, so
        // inspect it directly instead of parsing the whole thread on every tick.
        const raw = action.payload.messages;
        const rawLast = raw[raw.length - 1];
        const responseInFlight = rawLast ? isInFlightAssistantPlaceholder(rawLast) : false;

        // Parse + drop the backend's empty in-flight assistant placeholder so it
        // never renders as a blank bubble — and, when resuming, so its id can't
        // collide with the message the stream commits on `done`. The live
        // streaming buffer or the polling thinking-indicator stands in for that
        // turn. Only built when we actually reassign `messages`.
        // The persisted `companies` block (the saved form of the `companies` SSE
        // side-channel) is lifted onto msg.companies for the linkifier and
        // stripped from the visible blocks.
        const buildVisible = () =>
          raw
            .map((msg) => {
              const blocks = normalizeStructuredBlocks(msg.structured_blocks);
              const companiesBlock = blocks.find((b): b is CompaniesBlock => b.type === "companies");
              return {
                ...msg,
                structured_blocks: blocks.filter((b) => b.type !== "companies"),
                companies: companiesBlock?.items ?? [],
              };
            })
            .filter((msg) => !isInFlightAssistantPlaceholder(msg));

        // Keep the streaming buffer intact: if the loaded conversation is the one
        // this client is streaming, this resumes it and the buffer represents the
        // turn.
        if (streamMatchesActiveView(state)) {
          state.messages = buildVisible();
          state.status = "streaming";
          state.pollingConversationId = null;
          state.error = null;
        } else if (responseInFlight) {
          // A response is being generated in the background (another tab, or this
          // tab after a reload/disconnect). Show the thinking indicator and poll.
          // Steady poll ticks leave `messages` untouched (no flicker); only the
          // first detection populates the thread.
          if (!wasSelfPoll) state.messages = buildVisible();
          state.status = "awaiting_response";
          state.pollingConversationId = conversationId;
          state.error = null;
        } else {
          // Fully materialised conversation — the response (if any) has landed.
          state.messages = buildVisible();
          state.status = "idle";
          state.pollingConversationId = null;
          state.error = null;
        }
      })
      .addCase(loadConversation.rejected, (state, action) => {
        // Ignore a stale/superseded request's failure.
        if (action.meta.arg !== state.loadingConversationId) return;
        // A transient failure during polling shouldn't kill the wait — the next
        // tick retries. Only surface an error for a genuine (initial) load.
        if (state.status === "awaiting_response") return;
        state.status = "error";
        state.error = action.payload ?? "Failed to load conversation.";
      })

      .addCase(renameConversation.fulfilled, (state, action) => {
        const { conversationId, title } = action.payload;
        const conv = state.conversations.find((c) => c.id === conversationId);
        if (conv) conv.title = title;
        if (state.activeConversation && state.activeConversation.id === conversationId) {
          state.activeConversation.title = title;
        }
      })

      .addCase(deleteConversation.fulfilled, (state, action) => {
        const { conversationId } = action.payload;
        state.conversations = state.conversations.filter((c) => c.id !== conversationId);
        if (state.activeConversationId === conversationId) {
          state.activeConversationId = null;
          state.activeConversation = null;
          state.messages = [];
          state.streamingMessage = null;
          state.streamingConversationId = null;
          state.pollingConversationId = null;
          state.loadingConversationId = null;
          state.status = "idle";
          state.error = null;
        }
      });
  },
});

export const { streamStarted, conversationStarted, textDeltaAppended, textReset, toolCallStarted, toolCallResulted, structuredBlockReceived, companiesReceived, followUpsReceived, streamEnded, streamErrored, streamAborted, streamInProgressDetected, pollTimedOut, pollFailed, conversationCleared } = hermesSlice.actions;

// True when there is an in-flight stream AND it belongs to the conversation the
// user is currently viewing. Components must gate the streaming UI on this
// rather than on `status` alone, so a background stream for another conversation
// doesn't lock the composer or show a phantom streaming bubble in the open one.
export const selectIsActiveStreaming = (state: HermesRootState): boolean => streamMatchesActiveView(state.hermes) && state.hermes.streamingMessage !== null;

// True when a response for the conversation in view is being generated in the
// background and we're polling for it. Like streaming, it must lock the composer
// and show a thinking indicator — but there's no live stream to Stop.
export const selectIsAwaiting = (state: HermesRootState): boolean => state.hermes.status === "awaiting_response";

export default hermesSlice.reducer;
